import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { authAxios } from '../utils/api';
import { Mail, Lock, User, Eye, EyeOff, LogIn, UserPlus } from 'lucide-react';

// Color Palette:
// #1E3A4C - Dark teal/navy (primary dark)
// #FF7F50 - Coral/Orange (accent)
// #4682B4 - Steel blue (secondary)
// #F5F0E6 - Off-white/cream (background)

export default function LoginPage({ API, authUrl, onLoginSuccess }) {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  
  // Check for tab query parameter and load feature flags
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'register') {
      setActiveTab('register');
    }
    
    // Check if demo mode is enabled via feature flag
    const checkDemoFlag = async () => {
      try {
        const response = await fetch(`${API}/platform/feature-flags/public`);
        if (response.ok) {
          const flags = await response.json();
          const demoFlag = flags.find(f => f.id === 'demo_environment');
          setDemoEnabled(demoFlag?.enabled ?? false);
        }
      } catch (error) {
        // Default to disabled if can't fetch flags
        setDemoEnabled(false);
      }
    };
    checkDemoFlag();
  }, [searchParams, API]);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAxios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      // Store session token in localStorage for mobile compatibility
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      toast.success('Login successful!');
      // Handle both old format (user directly) and new format (user in object)
      const userData = response.data.user || response.data;
      onLoginSuccess(userData);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerName || !registerEmail || !registerPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (registerPassword !== registerConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!/\d/.test(registerPassword)) {
      toast.error('Password must contain at least 1 number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAxios.post(`${API}/auth/register`, {
        name: registerName,
        email: registerEmail,
        password: registerPassword
      });
      
      // Store session token in localStorage for mobile compatibility
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      toast.success('Account created successfully!');
      // Handle both old format (user directly) and new format (user in object)
      const userData = response.data.user || response.data;
      onLoginSuccess(userData);
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#F5F0E6' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("/sfl-bg.png")`,
        backgroundSize: '400px 400px',
        backgroundRepeat: 'repeat'
      }}></div>
      
      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">
        <div className="max-w-md mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/logo-new.png" 
              alt="Survivor Fantasy League" 
              className="w-32 h-32 mx-auto mb-4 drop-shadow-lg"
            />
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif', color: '#1E3A4C' }}>
              Survivor Fantasy League
            </h1>
            <p style={{ color: '#FF7F50' }} className="font-medium">Outwit • Outplay • Outlast</p>
          </div>

          {/* Login Card */}
          <Card className="border-2 shadow-xl" style={{ borderColor: '#FF7F50' }}>
            <CardHeader className="text-center pb-2">
              <CardTitle style={{ color: '#1E3A4C' }}>Welcome Back</CardTitle>
              <CardDescription style={{ color: '#4682B4' }}>Sign in to continue your adventure</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger 
                    value="login"
                    className="data-[state=active]:bg-[#FF7F50] data-[state=active]:text-white"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register"
                    className="data-[state=active]:bg-[#FF7F50] data-[state=active]:text-white"
                  >
                    Register
                  </TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" style={{ color: '#1E3A4C' }}>Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="you@example.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="pl-10 border-[#4682B4]/30 focus:border-[#FF7F50]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="login-password" style={{ color: '#1E3A4C' }}>Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="pl-10 pr-10 border-[#4682B4]/30 focus:border-[#FF7F50]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                          style={{ color: '#4682B4' }}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="text-right">
                        <Link 
                          to="/forgot-password" 
                          className="text-sm hover:underline"
                          style={{ color: '#4682B4' }}
                        >
                          Forgot password?
                        </Link>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full text-white" 
                      disabled={loading}
                      style={{ backgroundColor: '#FF7F50' }}
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="register-name" style={{ color: '#1E3A4C' }}>Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Your name"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          className="pl-10 border-[#4682B4]/30 focus:border-[#FF7F50]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-email" style={{ color: '#1E3A4C' }}>Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="you@example.com"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          className="pl-10 border-[#4682B4]/30 focus:border-[#FF7F50]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-password" style={{ color: '#1E3A4C' }}>Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                        <Input
                          id="register-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Min 8 chars, 1 number"
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          className="pl-10 pr-10 border-[#4682B4]/30 focus:border-[#FF7F50]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                          style={{ color: '#4682B4' }}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-confirm" style={{ color: '#1E3A4C' }}>Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                        <Input
                          id="register-confirm"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Confirm password"
                          value={registerConfirmPassword}
                          onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                          className="pl-10 border-[#4682B4]/30 focus:border-[#FF7F50]"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full text-white" 
                      disabled={loading}
                      style={{ backgroundColor: '#FF7F50' }}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: '#4682B4', opacity: 0.3 }}></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white" style={{ color: '#4682B4' }}>or continue with</span>
                </div>
              </div>

              {/* Social Login */}
              <div className="space-y-3">
                <a
                  href={authUrl}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 font-medium px-4 py-3 rounded-lg hover:bg-[#F5F0E6] transition-colors"
                  style={{ borderColor: '#1E3A4C', color: '#1E3A4C' }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </a>

                {/* Demo Mode Button - Only shown when feature flag is enabled */}
                {demoEnabled && (
                  <button
                    type="button"
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const response = await authAxios.post(`${API}/auth/login`, {
                          email: 'demo@survivor-league.com',
                          password: 'demo1234'
                        });
                        if (response.data.session_token) {
                          localStorage.setItem('session_token', response.data.session_token);
                        }
                        toast.success('Welcome to Demo Mode!');
                        const userData = response.data.user || response.data;
                        onLoginSuccess(userData);
                      } catch (error) {
                        console.error('Demo login failed:', error);
                        toast.error('Demo mode unavailable. Please try again later.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 font-medium px-4 py-3 rounded-lg transition-colors border-2"
                    style={{ 
                      backgroundColor: '#1E3A4C', 
                      color: 'white',
                      borderColor: '#1E3A4C'
                    }}
                    data-testid="btn-try-demo"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Try Demo (Read-Only)
                  </button>
                )}

              </div>

              <p className="text-xs text-center mt-4" style={{ color: '#4682B4' }}>
                By signing in, you agree to our terms of service and privacy policy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
